// Ultimate Card Game (UCG) - Bot AI strategist
//
// Picks the plan (gambit, js/engine/ai/gambits.js) the bot plays under, keeps
// an eye on whether it works, and moves on: a match is no longer one policy
// from the first second to the last but a few intentions in a row, chosen
// with a little curiosity and a memory of what has worked against this kind
// of opponent (docs/AI_MASTERPLAN_2026-10.md, 6.5).
//
//   opening     at match start, once the deck is dealt: a bandit (Thompson
//               sampling over Beta(wins, losses) per plan) among the plans the
//               deck can carry. A doctrine tilts the odds, a plan that ran in
//               the last matches is less likely again, and a tier-dependent
//               share of picks is plain curiosity.
//   during      every decision tick: success, abort and timebox are checked.
//               Plans are never dropped before `minCommit` seconds unless the
//               base is in danger - a human does not give up a plan after
//               three seconds.
//   afterwards  the next plan is drawn the same way (with planB) or the bot
//               plays neutral. Two plans have a follow-up: a beaten attack
//               turns Turtle into one big counter-push, a fired enemy
//               superweapon releases the held-back heavies at once.
//   match end   the result teaches the memory (win = the plans that ran won).
//
// Reflexes beat plans: the strategist only nudges scores (biasFor), the wave
// planner's thresholds (waveMods) and the superweapon threshold - it never
// plays a card, and every gate (anti-air need, the anti-suicide rules of the
// spells, critical base defence) stays in force.
//
// Everything random comes from the bot's rng, so seeded simulations replay.

import { GAMBITS, GAMBIT_IDS, STARTER_POOL, summarizeDeck } from './gambits.js';
import { enemyTeamOf } from './perception.js';
import { cardHasRole } from '../../data/aiCardHints.js';
import { sampleBeta } from './sampling.js';

export { sampleBeta };

export const STRATEGIST_DEFAULTS = Object.freeze({
  pool: 'all',        // 'all' or a list of gambit ids
  curiosity: 0.12,    // share of picks that ignore the statistics
  planB: true,        // pick another plan after one ends (else play neutral)
  minCommit: 25,      // seconds a plan is kept even if it looks bad (not when the base is in danger)
  reevalSec: 20,      // how often success / abort are looked at
  jitter: 0.3,        // +- share of reevalSec, so the rhythm is not a metronome
  opening: null       // eval tooling only: force this plan for the opening
});

const EMERGENCY_BASE_HP = 0.5;
// Caps what a plan can add to one card's score - reflexes stay stronger.
const MAX_BIAS = 30;
// What a plan did in the last matches makes it less likely again.
const REPEAT_PENALTY = [0.6, 0.8];
const MEMORY_DECAY = 0.97;
const COUNTERPUSH_SEC = 10;
const RECOVER_SEC = 12;

// --- memory ------------------------------------------------------------------

// What has worked against what: win / loss weights per (context, plan), and
// which plans ran in the last matches of a context. Lives as long as its owner
// (the skirmish bot lives for the session); js/engine/ai/opponentMemory.js
// keeps one per player in localStorage and sets `onChange` to write it through.
export class GambitMemory {
  constructor() {
    this.arms = new Map();     // `${key}|${id}` -> { w, l }
    this.recentPlans = new Map(); // key -> [[ids of the last match], [ids of the one before], ...]
    this.onChange = null;
  }

  stats(key, id) {
    return this.arms.get(`${key}|${id}`) || { w: 0, l: 0 };
  }

  record(key, id, reward) {
    const k = `${key}|${id}`;
    const s = this.arms.get(k) || { w: 0, l: 0 };
    s.w = s.w * MEMORY_DECAY + reward;
    s.l = s.l * MEMORY_DECAY + (1 - reward);
    this.arms.set(k, s);
    this.onChange?.();
  }

  rememberMatch(key, ids) {
    const list = this.recentPlans.get(key) || [];
    list.unshift([...new Set(ids)]);
    this.recentPlans.set(key, list.slice(0, 4));
    this.onChange?.();
  }

  recent(key) {
    return this.recentPlans.get(key) || [];
  }

  reset() {
    this.arms.clear();
    this.recentPlans.clear();
  }
}

// --- strategist ----------------------------------------------------------------

export class Strategist {
  constructor(bot, memory = new GambitMemory()) {
    this.bot = bot;
    this.memory = memory;
    this.config = null;
    // Set from outside, kept across matches: what the memory knows about this
    // player's habits, and the plan picked before the deck was built.
    this.habits = null;
    this.pendingOpening = null;
    this.reset();
  }

  // null = no plans (every tier until it is switched on), true = the defaults.
  setConfig(config) {
    if (!config) { this.config = null; return; }
    const given = config === true ? {} : config;
    this.config = { ...STRATEGIST_DEFAULTS, ...given };
  }

  get enabled() {
    return this.config !== null;
  }

  reset() {
    this.active = GAMBITS.standard;
    this.startedAt = 0;
    this.history = [];
    this.stats = this._freshStats();
    this.ctx = null;
    this.nextEvalAt = 0;
    this.counterpushUntil = 0;
    this.realWaveAt = null;
    this.recoverUntil = 0;
    this._prevEnemyNukeLive = null;
    this._enemyNukeFiredAt = null;
    this._deck = null;
    this._outcomeRecorded = false;
    this._started = false;
  }

  _freshStats() {
    return { hypersPlayed: 0, airPlayed: 0, overstimulationCast: false, frontStructuresAtDepth: 0, enemyAttackSeen: false, enemyInOwnHalfPeak: 0, defenseWonAt: null, firstAttackAt: null, rushedEarly: false, firstAirAt: null,
      economyPlayed: false, metamorphosisCast: false, mentalExtractionCast: false, outrangersPlayed: 0, slingPlayed: false, shadesPlayed: 0, wavesAtStart: 0 };
  }

  // The deck is dealt by now: draw the opening plan. Called from resetForMatch().
  begin() {
    this.reset();
    if (!this.enabled) return;
    const { cardSystem, team } = this.bot;
    const deck = team === 'western' ? cardSystem?.playerDeck : cardSystem?.enemyDeck;
    this._deck = summarizeDeck(deck);
    this._started = true;
    // `opening` (eval tooling): force this plan if the deck can carry it.
    const forced = this.config.opening;
    // The plan chosen before the deck was built (deck coupling) opens the match if the dealt deck carries it.
    const coupled = this.pendingOpening;
    this.pendingOpening = null;
    const open = forced && this._eligible(new Set()).includes(forced) ? GAMBITS[forced]
      : (forced ? GAMBITS.standard
        : (coupled && this._eligible(new Set()).includes(coupled) ? GAMBITS[coupled] : this._choose(new Set())));
    this._switchTo(open, 0, 'opening');
  }

  // --- choosing -----------------------------------------------------------------

  get key() {
    const { bot } = this;
    const race = bot.battlefield?.bases?.[bot.team]?.race || 'any';
    const threat = bot.opponentProfile?.primaryThreat || 'unknown';
    return `${race}|${bot.difficulty}|${threat}`;
  }

  _eligible(exclude) {
    const cfg = this.config;
    const ids = cfg.pool === 'all' ? GAMBIT_IDS : cfg.pool;
    const race = this.bot.battlefield?.bases?.[this.bot.team]?.race;
    return ids.filter(id => {
      const g = GAMBITS[id];
      if (!g || id === 'standard' || exclude.has(id)) return false;
      if (g.races !== 'any' && race && !g.races.includes(race)) return false;
      return g.requires(this._deck);
    });
  }

  // How much the memory counts, 0..1 (profile.memoryInfluence; the harness, which
  // has no profile, lets it count in full).
  get influence() {
    return this.bot.profile?.memoryInfluence ?? 1;
  }

  // What the bot has seen this player do makes some plans more attractive: a
  // rusher is answered by Turtle, a turtle by pushing structures, an early
  // superweapon by Bait, a player without air leaves the sky open.
  habitFactor(id) {
    const h = this.habits;
    if (!h || !this.influence) return 1;
    let f = 1;
    if (id === 'turtleCounter') f += 0.8 * h.rushRate;
    else if (id === 'fortressFront') f += 0.6 * h.turtleRate;
    else if (id === 'nukeBait') f += 0.8 * (h.nukeRate > 0.5 && h.nukeAt !== null && h.nukeAt < 150 ? h.nukeRate : 0);
    else if (id === 'airSwitch') f += 0.5 * (1 - h.airRate);
    return 1 + (f - 1) * this.influence;
  }

  // Thompson sampling among `eligible` plus "no plan"; a slice of the picks is curiosity.
  _pick(eligible, key) {
    const { rng, doctrineId } = this.bot;
    if (eligible.length === 0) return GAMBITS.standard;
    const recent = this.memory.recent(key);
    const penalty = (id) => {
      let m = 1;
      recent.slice(0, REPEAT_PENALTY.length).forEach((ids, i) => { if (ids.includes(id)) m *= REPEAT_PENALTY[i]; });
      return m;
    };
    if (rng.next() < this.config.curiosity) {
      const fresh = eligible.filter(id => penalty(id) === 1);
      return GAMBITS[rng.pick(fresh.length ? fresh : eligible)];
    }
    const k = this.influence;
    let best = null;
    let bestScore = -1;
    for (const id of [...eligible, 'standard']) {
      const s = this.memory.stats(key, id);
      const prior = GAMBITS[id].prior?.[doctrineId] || 0;
      const draw = sampleBeta(1 + s.w * k, 1 + s.l * k, rng) * (1 + prior) * this.habitFactor(id) * penalty(id) * (id === 'standard' ? 0.8 : 1);
      if (draw > bestScore) { bestScore = draw; best = id; }
    }
    return GAMBITS[best];
  }

  _choose(exclude) {
    return this._pick(this._eligible(exclude), this.key);
  }

  // Before the deck is built (procedural tiers): the plan this match is meant
  // to carry, so the deck can be built for it (js/data/aiDecks.js). It opens the
  // match if the dealt deck turns out to carry it. Returns the gambit id or null.
  preview(race, opponentProfile = null) {
    this.pendingOpening = null;
    if (!this.enabled) return null;
    const cfg = this.config;
    const ids = (cfg.pool === 'all' ? GAMBIT_IDS : cfg.pool).filter(id => {
      const g = GAMBITS[id];
      return g && id !== 'standard' && g.core && g.core(race, []) !== null && (g.races === 'any' || g.races.includes(race));
    });
    const key = `${race}|${this.bot.difficulty}|${opponentProfile?.primaryThreat || 'unknown'}`;
    const picked = this._pick(ids, key);
    this.pendingOpening = picked.id === 'standard' ? null : picked.id;
    return this.pendingOpening;
  }

  _switchTo(gambit, t, reason) {
    if (this.active && this.active.id !== 'standard' && this.history.length) {
      const last = this.history[this.history.length - 1];
      if (last.end === null) { last.end = t; last.reason = reason; }
    }
    this.active = gambit;
    this.startedAt = t;
    this.history.push({ id: gambit.id, start: t, end: null, reason: null });
    this.stats.hypersPlayed = 0;
    this.stats.airPlayed = 0;
    this.stats.overstimulationCast = false;
    this.stats.frontStructuresAtDepth = 0;
    this.stats.economyPlayed = false;
    this.stats.metamorphosisCast = false;
    this.stats.mentalExtractionCast = false;
    this.stats.outrangersPlayed = 0;
    this.stats.slingPlayed = false;
    this.stats.shadesPlayed = 0;
    this.stats.wavesAtStart = this._wavesReleased();
    this.nextEvalAt = t + this._evalInterval();
  }

  _evalInterval() {
    const { reevalSec, jitter } = this.config;
    return reevalSec * (1 + (this.bot.rng.next() * 2 - 1) * jitter);
  }

  // --- events -----------------------------------------------------------------

  // The bot just played `card`.
  onPlay(card) {
    if (!this.enabled || !card) return;
    if (card.type === 'Melt') this.stats.hypersPlayed++;
    if (card.isAir || card.elementType === 'Air') this.stats.airPlayed++;
    if (card.spellEffect === 'forced_overstimulation') this.stats.overstimulationCast = true;
    if (card.name === 'The Salvage Company' || card.name === 'Claiming Pillar') this.stats.economyPlayed = true;
    if (card.spellEffect === 'experimental_metamorphosis') this.stats.metamorphosisCast = true;
    if (card.spellEffect === 'mental_extraction') this.stats.mentalExtractionCast = true;
    if (!card.isStructure && cardHasRole(card, 'outranger')) this.stats.outrangersPlayed++;
    if (card.name === 'Invasion Capsule Sling') this.stats.slingPlayed = true;
    if (card.name === 'Shade' || card.name === 'Hyper Shade') this.stats.shadesPlayed++;
    if ((card.deployAtBonusIndicator || card.name === 'Fodder') && (this.ctx?.pushRatio ?? 0) >= 0.25) this.stats.frontStructuresAtDepth++;
  }

  // How many waves the planner has released so far (cap, max, estimator, threshold).
  _wavesReleased() {
    const r = this.bot.waves?.releases;
    return r ? Object.values(r).reduce((a, b) => a + b, 0) : 0;
  }

  _buildContext(view, t) {
    const { bot } = this;
    const enemy = enemyTeamOf(bot.team);
    const { battlefield } = bot;
    const tac = view.tactical;
    const ownBase = battlefield.bases?.[bot.team];
    const enemyBase = battlefield.bases?.[enemy];
    const midX = battlefield.width / 2;
    const divider = battlefield.territoryDivider ?? midX;
    const deadZone = 15;
    const maxDist = Math.max(1, midX - 46 - deadZone);
    const adv = bot.team === 'western' ? divider - (midX + deadZone) : (midX - deadZone) - divider;
    const pushRatio = Math.max(0, Math.min(1, adv / maxDist));
    let enemyAntiAir = 0;
    let enemyDetectors = 0;
    for (const u of tac.visibleEnemies) {
      if (u.targetsAir !== false && (u.damage || 0) > 0 && !u.isParachuting) enemyAntiAir++;
      if (cardHasRole(u.cardData || { name: u.name }, 'detector')) enemyDetectors++;
    }

    const live = bot.superweapons.get(enemy) > 0;
    if (this._prevEnemyNukeLive === true && !live && this._enemyNukeFiredAt === null) this._enemyNukeFiredAt = t;
    this._prevEnemyNukeLive = live;

    const s = this.stats;
    s.enemyInOwnHalfPeak = Math.max(s.enemyInOwnHalfPeak, tac.enemyInOwnHalf);
    if (tac.enemyInOwnHalf >= 15) { s.enemyAttackSeen = true; if (s.firstAttackAt === null) s.firstAttackAt = t; }
    if (t < 75 && tac.enemyInOwnHalf >= 25) s.rushedEarly = true;
    if (s.firstAirAt === null && (bot.enemyMemory?.recentAirTime ?? -999) > -900) s.firstAirAt = t;
    if (s.defenseWonAt === null && s.enemyInOwnHalfPeak >= 25 && tac.enemyInOwnHalf <= 5) s.defenseWonAt = t;

    this.ctx = {
      t,
      tSince: t - this.startedAt,
      baseHp: ownBase ? ownBase.hp / (ownBase.maxHp || 1) : 1,
      enemyBaseHp: enemyBase ? enemyBase.hp / (enemyBase.maxHp || 1) : 1,
      pushRatio,
      ownValue: tac.ownValue,
      enemyValue: tac.enemyValue,
      enemyInOwnHalf: tac.enemyInOwnHalf,
      enemyAntiAir,
      enemyDetectors,
      wavesReleased: this._wavesReleased(),
      enemyNukeLive: live,
      enemyNukeFiredAt: this._enemyNukeFiredAt,
      stats: s
    };
    return this.ctx;
  }

  // Every decision tick. `view` is the tick's BoardView.
  tick(view) {
    if (!this.enabled || !this._started) return;
    const t = this.bot.gameTime;
    const ctx = this._buildContext(view, t);
    const g = this.active;

    if (this.realWaveAt !== null && t >= this.realWaveAt) {
      this.realWaveAt = null;
      this.bot.waves?.forceRelease?.(3);
    }

    // A fired enemy superweapon frees the held-back heavies, whatever the plan.
    if (g.id === 'nukeBait' && ctx.enemyNukeFiredAt !== null && this.recoverUntil === 0) {
      this.recoverUntil = t + RECOVER_SEC;
      this.bot.waves?.forceRelease?.(4);
    }

    if (g.id === 'standard') {
      // Neutral stretches do not last: look for a plan again at the next review.
      if (t >= this.nextEvalAt) {
        this.nextEvalAt = t + this._evalInterval();
        if (this.config.planB && this.history.length < 6) this._advance(t, 'neutral review', false);
      }
      return;
    }

    const emergency = ctx.baseHp < EMERGENCY_BASE_HP;
    const due = t >= this.nextEvalAt || emergency;
    if (!due) return;
    this.nextEvalAt = t + this._evalInterval();

    const timedOut = ctx.tSince >= g.timeboxSec;
    const succeeded = g.success(ctx);
    const aborted = g.abort(ctx) || timedOut;
    if (succeeded) {
      if (g.id === 'turtleCounter') this.counterpushUntil = t + COUNTERPUSH_SEC;
      // The feint worked (or not): the real wave follows a few seconds later.
      if (g.id === 'feint') this.realWaveAt = t + 4 + this.bot.rng.next() * 4;
      this._advance(t, 'success', true);
    } else if (aborted && (emergency || ctx.tSince >= this.config.minCommit || timedOut)) {
      this._advance(t, timedOut ? 'timebox' : 'abort', true);
    }
  }

  // Moves on from the current plan: another one (planB) or neutral.
  _advance(t, reason, exclusive) {
    const used = new Set(this.history.map(h => h.id));
    const exclude = exclusive ? new Set([this.active.id, ...used]) : new Set(this.active.id === 'standard' ? [] : [this.active.id]);
    const next = this.config.planB ? this._choose(exclude) : GAMBITS.standard;
    this._switchTo(next, t, reason);
  }

  // --- what the plan changes -------------------------------------------------------

  get intent() {
    return this.active.intent;
  }

  get gambitId() {
    return this.active.id;
  }

  // Score points for a card under the current plan, capped.
  biasFor(card) {
    if (!this.enabled || !this.ctx) return 0;
    let points = this.active.intent.bias(card, this.ctx) || 0;
    if (this.recoverUntil > 0 && this.bot.gameTime < this.recoverUntil && (card.cost || 0) >= 30) points += 20;
    return Math.max(-MAX_BIAS, Math.min(MAX_BIAS, points));
  }

  // Is `card` kept in hand right now? The visible part of a plan. Never while
  // the base is in danger or an attack is on, and never when so much of the
  // hand is held that it would clog (a human does not hoard the whole hand).
  holds(card, hand) {
    if (!this.enabled || !this.ctx || !hand) return false;
    const c = this.ctx;
    if (c.baseHp < 0.6 || c.enemyInOwnHalf >= 20) return false;
    const rule = this.active.intent.hold;
    if (!rule || !rule(card, c, hand)) return false;
    let held = 0;
    for (const x of hand) if (x && !x.hidden && !x.pending && rule(x, c, hand)) held++;
    return held <= 2;
  }

  pairHoldBias() {
    return this.enabled ? (this.active.intent.pairHoldBias || 0) : 0;
  }

  // { thresholdMul, defendFactorMul } for the wave planner, or null.
  waveMods() {
    if (!this.enabled) return null;
    const t = this.bot.gameTime;
    if (t < this.counterpushUntil) return { thresholdMul: 0.6, defendFactorMul: 1 };
    return this.active.intent.waves || null;
  }

  nukeThetaOffset() {
    return this.enabled ? (this.active.intent.nukeThetaOffset || 0) : 0;
  }

  // --- learning ------------------------------------------------------------------

  // The match ended; `won` from this bot's side. Teaches the memory once.
  recordOutcome(won) {
    if (!this.enabled || !this._started || this._outcomeRecorded) return;
    this._outcomeRecorded = true;
    const key = this.key;
    const ids = this.history.map(h => h.id);
    for (const id of new Set(ids)) this.memory.record(key, id, won ? 1 : 0);
    this.memory.rememberMatch(key, ids.filter(id => id !== 'standard'));
  }

  // What the bot SAW the opponent do this match (observable behaviour only), for
  // the memory: an early rush, early air, a long peaceful opening, the second
  // their superweapon went off.
  observedHabits() {
    const s = this.stats;
    return {
      rushed: s.rushedEarly,
      airEarly: s.firstAirAt !== null && s.firstAirAt < 100,
      turtled: this.bot.gameTime >= 120 && (s.firstAttackAt === null || s.firstAttackAt > 120),
      nukeFiredAt: this._enemyNukeFiredAt
    };
  }

  // The plans this match went through, for telemetry and tests (never shown).
  planLog() {
    return this.history.map(h => ({ ...h }));
  }
}

export { STARTER_POOL };
